import { noop } from 'lodash-es';
import { createContext, useContext, useRef, type PropsWithChildren, type ReactNode } from 'react';
import { ButtonVariants } from '../components/Button/styles';
import type { DialogProps } from '../components/Dialog/types';
import { useCallbackRef } from '../hooks';
import { useDisclosure, type UseDisclosureReturn } from '../hooks/useDisclosure';

interface OpenGlobalDialogProps extends Partial<DialogProps> {
  content: ReactNode;
}

const GlobalDialogContext = createContext<
  | ({
      content?: ReactNode;
      open: (params?: OpenGlobalDialogProps) => void;
    } & DialogProps &
      Omit<UseDisclosureReturn, 'open'>)
  | undefined
>(undefined);

/**
 * Hook to open global dialogs that are not tied to a specific component.
 * @returns The global dialog context, which can be used to manage a global dialog.
 */
export const useGlobalDialog = () => {
  const context = useContext(GlobalDialogContext);
  if (context === undefined) {
    throw new Error('Missing Dialog.Provider further up in the tree. Did you forget to add it?');
  }
  return context;
};

const DEFAULT_PARAMS = {
  onConfirm: noop,
  onCancel: noop,
  content: undefined,
  confirmLabel: 'Yes',
  cancelLabel: 'No',
  showClose: false,
  showCancel: true,
  showConfirm: true,
  closeOnClickOutside: true,
  stretchButtons: true,
  variant: ButtonVariants.Primary,
};

/**
 * Provider is used to open global dialogs that are not tied to a specific component.
 */
export const GlobalDialogProvider = function GlobalDialogProvider({ children }: PropsWithChildren) {
  const paramsRef = useRef<Partial<OpenGlobalDialogProps>>();

  // TODO: parameterize more props as needed, starting with most common for now
  // NOTE: usePortal is not supported. This Dialog is on top of the DOM chain which means that we can't actually place it inline the local component
  const dialog = useDisclosure();

  const openDialog = useCallbackRef((params?: OpenGlobalDialogProps) => {
    paramsRef.current = { ...DEFAULT_PARAMS, ...params };

    dialog.open();
  });

  return (
    <GlobalDialogContext.Provider
      value={{
        ...dialog,
        open: openDialog,
        ...paramsRef.current,
      }}
    >
      {children}
    </GlobalDialogContext.Provider>
  );
};
